import {
  Component,
  inject,
  input,
  model,
  OnInit,
  output,
  signal,
  ChangeDetectionStrategy,
} from '@angular/core';
import {
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { AddEmailComponent } from '@app/modules/service-data/components/approve-document-dialog/add-email/add-email.component';
import { NotificationProfileModel, TemplateType } from '@core/core.types';
import { replaceNotificationEmailTemplateTerms } from '@core/utils/core.util';
import {
  NgbActiveModal,
  NgbModal,
  NgbTooltip,
} from '@ng-bootstrap/ng-bootstrap';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { DataTableComponent } from '@shared/components/data-table/data-table.component';
import {
  DataTableActionsLocation,
  DataTableActionType,
  DataTableConfiguration,
  DataTableSelectionType,
} from '@shared/components/data-table/data-table.types';
import { BehaviorSubject } from 'rxjs';
import {
  NotificationEmailConfig,
  Report,
} from '../../../../types/report.types';

enum RecipientType {
  TO = 'To',
  CC = 'Cc',
  BCC = 'Bcc',
}

@Component({
  selector: 'app-approve-report-prepare-email',
  imports: [
    TranslatePipe,
    FormsModule,
    ReactiveFormsModule,
    DataTableComponent,
    NgbTooltip,
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  templateUrl: './approve-report-prepare-email.component.html',
})
export class ApproveReportPrepareEmailComponent implements OnInit {
  protected modalRef = inject(NgbActiveModal);
  private ngbModal = inject(NgbModal);
  private translateService = inject(TranslateService);

  public kitId = input<string>(null);
  public report = input<Report>(null);
  public notificationProfile = input<NotificationProfileModel>(null);
  public templateType = input<TemplateType>(null);
  public emailTemplate = input<{
    serviceName: string;
    predefinedRecipients: any;
    subject: string;
    body: string;
  }>(null);
  public isLoading = model<boolean>(false);
  public emailConfig = output<NotificationEmailConfig>();

  public error = signal<string>(null);

  protected toDataTableConfiguration: DataTableConfiguration;
  protected ccDataTableConfiguration: DataTableConfiguration;
  protected bccDataTableConfiguration: DataTableConfiguration;

  protected sendEmailForm: FormGroup;
  protected toEmailInput = new FormControl('', [Validators.email]);
  protected ccEmailInput = new FormControl('', [Validators.email]);
  protected bccEmailInput = new FormControl('', [Validators.email]);

  private toEmails$ = new BehaviorSubject<{ email: string }[]>([]);
  private ccEmails$ = new BehaviorSubject<{ email: string }[]>([]);
  private bccEmails$ = new BehaviorSubject<{ email: string }[]>([]);

  ngOnInit(): void {
    let recipientsEmails = this.notificationProfile()
      ? this.notificationProfile()?.recipients.map((r) => ({
          email: r.recipient,
        }))
      : [];
    if (
      this.emailTemplate() &&
      this.templateType() !== TemplateType.NotQualified &&
      this.templateType() !== TemplateType.Canceled
    ) {
      if (
        this.emailTemplate()?.predefinedRecipients &&
        this.emailTemplate()?.predefinedRecipients.to
      ) {
        recipientsEmails = [
          ...recipientsEmails,
          ...this.emailTemplate()?.predefinedRecipients.to,
        ];
      }
      this.toEmails$.next(recipientsEmails);

      if (this.emailTemplate()?.predefinedRecipients.cc) {
        this.ccEmails$.next(
          this.emailTemplate()?.predefinedRecipients.cc.map(
            (email: string) => ({
              email,
            })
          )
        );
      }

      if (this.emailTemplate()?.predefinedRecipients.bcc) {
        this.bccEmails$.next(
          this.emailTemplate()?.predefinedRecipients.bcc.map(
            (email: string) => ({
              email,
            })
          )
        );
      }

      this.setupSendEmailNotificationsForm();
    }
  }

  protected previewEmail() {
    this.emailConfig.emit(this.validateAndSetNotificationEmailConfig());

    this.isLoading.set(false);
  }

  protected cancel() {
    this.emailConfig.emit(null);
  }

  protected addEmailByType(type: RecipientType) {
    switch (type) {
      case RecipientType.TO:
        this.addEmail(this.toEmailInput.value, this.toEmails$);
        this.toEmailInput.reset();
        break;
      case RecipientType.CC:
        this.addEmail(this.ccEmailInput.value, this.ccEmails$);
        this.ccEmailInput.reset();
        break;
      case RecipientType.BCC:
        this.addEmail(this.bccEmailInput.value, this.bccEmails$);
        this.bccEmailInput.reset();
        break;
    }
  }

  private validateAndSetNotificationEmailConfig(): NotificationEmailConfig {
    const emailSubject = this.sendEmailForm.get('subject').value;
    const emailBody = this.sendEmailForm.get('body').value;

    if (!emailSubject || !emailBody) {
      this.error.set(
        this.translateService.instant(
          'reports.approve.prepareEmail.errors.pleaseEnterEmailSubjectAndBody'
        )
      );
    }

    const [to, cc, bcc] = [
      this.toEmails$.value,
      this.ccEmails$.value,
      this.bccEmails$.value,
    ];

    if (to.length < 1) {
      this.error.set(
        this.translateService.instant(
          'reports.approve.prepareEmail.errors.pleaseAddAtLeastOneRecipient'
        )
      );
    }

    if (this.templateType() === TemplateType.HighRisk) {
      if (!this.report()?.reportReason) {
        this.error.set(
          this.translateService.instant(
            'reports.approve.prepareEmail.errors.pleaseEnterHighRiskReason'
          )
        );
      }
    }

    return {
      to: to.map((data) => data.email),
      cc: cc.map((data) => data.email),
      bcc: bcc.map((data) => data.email),
      subject: replaceNotificationEmailTemplateTerms(
        emailSubject,
        this.emailTemplate()?.serviceName,
        this.kitId(),
        this.templateType() === TemplateType.HighRisk
          ? this.report()?.reportReason
          : null
      ),
      body: replaceNotificationEmailTemplateTerms(
        emailBody,
        this.emailTemplate()?.serviceName,
        this.kitId(),
        this.templateType() === TemplateType.HighRisk
          ? this.report()?.reportReason
          : null
      ),
    };
  }

  private setupSendEmailNotificationsForm() {
    this.toDataTableConfiguration = {
      title: '',
      selectionMode: DataTableSelectionType.NONE,
      tableHeadClasses: '',
      actionsLocation: DataTableActionsLocation.RIGHT,
      singleActions: [
        {
          name: 'Remove',
          icon: 'fa-light fa-xmark',
          action: (e, row) => {
            this.removeEmail(row.email, this.toEmails$);
          },
          type: DataTableActionType.BUTTON,
        },
      ],
      columns: [
        {
          label: 'notificationProfilesPage.toEmailRecipients',
          property: 'email',
        },
      ],
      rows$: this.toEmails$,
    };

    this.ccDataTableConfiguration = {
      title: '',
      selectionMode: DataTableSelectionType.NONE,
      tableHeadClasses: '',
      actionsLocation: DataTableActionsLocation.RIGHT,
      singleActions: [
        {
          name: 'Remove',
          icon: 'fa-light fa-xmark',
          action: (e, row) => {
            this.removeEmail(row.email, this.ccEmails$);
          },
          type: DataTableActionType.BUTTON,
        },
      ],
      columns: [
        {
          label: 'notificationProfilesPage.ccEmailRecipients',
          property: 'email',
        },
      ],
      rows$: this.ccEmails$,
    };

    this.bccDataTableConfiguration = {
      title: '',
      selectionMode: DataTableSelectionType.NONE,
      tableHeadClasses: '',
      actionsLocation: DataTableActionsLocation.RIGHT,
      singleActions: [
        {
          name: 'Remove',
          icon: 'fa-light fa-xmark',
          action: (e, row) => {
            this.removeEmail(row.email, this.bccEmails$);
          },
          type: DataTableActionType.BUTTON,
        },
      ],
      columns: [
        {
          label: 'notificationProfilesPage.bccEmailRecipients',
          property: 'email',
        },
      ],
      rows$: this.bccEmails$,
    };

    this.sendEmailForm = new FormGroup<{
      subject?: FormControl<string>;
      body?: FormControl<string>;
      reason?: FormControl<string>;
    }>({
      subject: new FormControl('', [Validators.required]),
      body: new FormControl('', [Validators.required]),
    });

    if (this.templateType() === TemplateType.HighRisk) {
      this.sendEmailForm.addControl(
        'reason',
        new FormControl(this.report()?.reportReason, Validators.required)
      );
    }

    if (this.emailTemplate()?.subject) {
      this.sendEmailForm.get('subject').setValue(this.emailTemplate()?.subject);
    }

    if (this.emailTemplate()?.body) {
      this.sendEmailForm.get('body').setValue(this.emailTemplate()?.body);
    }
  }

  private openAddEmailDialog(title) {
    this.ngbModal
      .open(AddEmailComponent, {
        centered: true,
        modalDialogClass: 'modal-size-sm',
        size: 'sm',
      })
      .closed.subscribe((resp) => {
        if (!resp?.email) {
          return;
        }

        switch (title) {
          case RecipientType.TO:
            this.addEmail(resp.email, this.toEmails$);
            break;
          case RecipientType.CC:
            this.addEmail(resp.email, this.ccEmails$);
            break;
          case RecipientType.BCC:
            this.addEmail(resp.email, this.bccEmails$);
            break;
        }
      });
  }

  private addEmail(email: string, list$: BehaviorSubject<{ email: string }[]>) {
    list$.next([...list$.value, { email }]);
  }

  private removeEmail(
    email: string,
    list$: BehaviorSubject<{ email: string }[]>
  ) {
    const currentEmails = list$.value;
    const filteredEmails = currentEmails.filter((e) => e.email !== email);

    if (currentEmails.length !== filteredEmails.length) {
      list$.next(filteredEmails);
    }
  }

  protected readonly TemplateType = TemplateType;
  protected readonly RecipientType = RecipientType;
}
